import { useState, useRef, useEffect } from 'react';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { debounce } from 'lodash';

import Header from 'app/components/Header';
import Toolbar from 'app/components/Toolbar';
import ErrorModal from 'app/components/ErrorModal';

import {
  searchGdprSubject,
  anonymizeGdprSubject,
  downloadGdprSubject
} from 'app/services/gdpr';

const SearchField = props => {
  const {
    subject,
    setSubject,
    setHits,
    setError
  } = props;

  // using an uncontrolled input but setting the value when changed
  const inputRef = useRef();
  const onChangeDisabledRef = useRef(false);

  const setInputValue = value => {
    if (inputRef.current) {
      onChangeDisabledRef.current = true;

      let input = inputRef.current.querySelector('input');
      input.value = value;

      onChangeDisabledRef.current = false;
    }
  }

  useEffect(() => {
    if (inputRef.current) {
      if (subject) {
        setInputValue(subject.email);
      } else if (inputRef.current.value) {
        setInputValue(null);
      }
    }
  }, [subject]);

  const onChange = debounce(async event => {
    if (onChangeDisabledRef.current) {
      return;
    }

    let email = event.target.value;

    try {
      let resp = await searchGdprSubject({ email });

      if (resp.hits) {
        setHits(resp.hits);
      } else {
        setSubject(resp);
      }
    } catch(error) {
      setError(error);
    }
  }, 1000);

  const onClear = () => {
    setSubject(null);
    setInputValue(null);
  }

  let startAdornment = (
    <InputAdornment position="start" sx={{ marginRight: '0.5rem' }}>
      <SearchIcon />
    </InputAdornment>
  )

  let endAdornment = subject && (
    <InputAdornment position="end">
      <IconButton title="Clear search" onClick={onClear}>
        <CloseIcon />
      </IconButton>
    </InputAdornment>
  )

  return (
    <TextField
      ref={inputRef}
      label="Search Data Subject"
      helperText="Enter an email, phone number or access code"
      variant="outlined"
      defaultValue={subject?.email}
      onChange={onChange}
      InputProps={{
        startAdornment: startAdornment,
        endAdornment: endAdornment
      }}
    />
  )
}

const Subject = props => {
  const {
    subject
  } = props;

  let primaryButton = {
    label: 'Delete / Anonymize',
    icon: <DeleteIcon />,
    onClick: () => {
      anonymizeGdprSubject(subject.user_ids).then((resp) => {
        if (resp.needs_review) {
          alert('This GDPR request needs review: ' + resp.note);
        } else {
          alert('Subject data has been anonymized. To delete Stripe\'s data they\'ll need to be contacted at privacy@stripe.com. GDPR Request UUID: ' + resp.uuid);
        }
      }).catch(err => {
        alert(err.error);
      })
    },
    key: 'anonymize'
  }

  let buttons = [
    {
      label: 'Delete / Anonymize (force)',
      icon: <DeleteOutlineIcon />,
      onClick: () => {
        anonymizeGdprSubject(subject.user_ids, true).then((resp) => {
          alert('Subject data has been anonymized. To delete Stripe\'s data they\'ll need to be contacted at privacy@stripe.com. GDPR Request UUID: ' + resp.uuid);
        }).catch(err => {
          alert(err.error);
        })
      },
      key: 'force'
    },
    {
      label: 'Download',
      icon: <FileDownloadIcon />,
      onClick: () => downloadGdprSubject(subject.user_ids),
      key: 'download'
    }
  ]

  return (
    <>
      <Toolbar {...{ primaryButton, buttons }} />
      <pre style={{ height: 'calc(100vh - 250px)'}}>{JSON.stringify(subject, null, 2)}</pre>
    </>
  )
}

const Hits = props => {
  const {
    hits,
    onHitSelect
  } = props;

  return (
    <>
      <p style={{ margin: '1rem 0 0.5rem', fontWeight: 600 }}>Multiple results, please select one:</p>
      <List>
        {hits.map((hit, i) => (
          <ListItemButton key={`hit-${i}`} onClick={() => onHitSelect(hit)}>
            <ListItemText
              primary={hit.email}
              secondary={`User ID: ${hit.id}`}
            />
          </ListItemButton>
        ))}
      </List>
    </>
  )
}

const Gdpr = () => {
  const [state, setState] = useState({
    subject: null,
    hits: null
  });

  const setSubject = subject => {
    setState({
      subject,
      hits: null
    });
  }

  const setHits = hits => {
    setState({
      hits,
      subject: null
    });
  }

  const onHitSelect = async hit => {
    try {
      let resp = await searchGdprSubject({ user_id: hit.id });
      setSubject(resp);
    } catch(error) {
      setError(error);
    }
  }

  const [error, setError] = useState(null);
  const clearError = () => setError(null);

  return (
    <Container>
      <Header header="GDPR" fullWidth={true} />
      <Divider sx={{ margin: '0.5rem 0 1.5rem' }} />
      <SearchField {...{ subject: state.subject, setSubject, setHits, setError }} />
      {state.subject && <Subject subject={state.subject} />}
      {state.hits && <Hits {...{ hits: state.hits, onHitSelect }} />}
      <ErrorModal error={error} onClose={clearError} />
    </Container>
  )
}

export default Gdpr;
